import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'newComponents/Modal';
import { ModalBody, Header, Title } from 'newComponents/Modal/styled';
import { Button } from 'components/Button';
import { FormGroup } from 'components/Form/subcomponents/FormGroup';
import { StyledInput } from 'newComponents/Form/Input/styled';
import {
  AvisoUltimaTentativa,
  Content,
  ErroInline,
  Footer,
  Identificacao,
  LinkOutraConta,
  LoginUsuario,
  Message,
  NomeUsuario,
} from './styled';

/* ===== MARINERS-2011 — modal de re-autenticação ==============================
 *
 * Presentational: não conhece o store nem a fila. Quem liga os fios é o Host.
 *
 * ── Por que NÃO usa o `FormModal` ────────────────────────────────────────────
 * O `FormModal` já vem com `closeOnEsc={false}` e `closeOnOverlayClick={false}` —
 * ou seja, ESC e clique-fora já estavam cobertos. O que sobra nele é o **X do
 * header**, que chama `setClose` e não tem prop para desligar. Sem sessão não há
 * app utilizável por trás: fechar a modal deixaria o usuário olhando uma tela
 * morta, com as requests represadas para sempre.
 *
 * Então monta-se sobre o `Modal` de baixo nível com as MESMAS peças de estilo
 * (`ModalBody`/`Header`/`Title`) — mesma casca visual da UpdateModal, sem o X.
 * Mexer no `FormModal` para ganhar um `hideClose` afetaria todas as modais do
 * produto por causa de um caso; o custo certo é este componente carregar as três
 * linhas de layout.
 *
 * A única saída é deliberada e explícita: "Entrar com outra conta", que faz
 * logout completo.
 * ========================================================================== */

/* ⚠️ `FormGroup` + `StyledInput` diretos, e não o `Input` de newComponents/Form.
 *
 * O `Input` espalha `{...props}` DUAS vezes — no `FormGroup` e no `StyledInput` — e o
 * `FormGroup` repassa para o `StyledField`. Resultado: um `data-testid` passado a ele
 * aparece em DOIS elementos, e `getByTestId` estoura com "found multiple". O e2e deste
 * ticket depende de `reauth-password` ser único.
 *
 * Montar as duas peças aqui dá o mesmo visual (são os mesmos styled-components) com
 * controle do seletor. Perde-se o olho de revelar senha do `Input` — aceitável num prompt
 * de sessão, e não vale corrigir o spread duplicado do componente compartilhado por
 * dentro deste ticket. */
const TS_FormGroup = FormGroup as unknown as React.FC<Record<string, unknown>>;

export interface ReauthModalProps {
  /** Nome de exibição do usuário da sessão caída. */
  nomeExibicao: string | null;
  /** Login (o que vai no POST). Mostrado como confirmação, nunca editável. */
  login: string | null;
  tentativasRestantes: number;
  erro: string | null;
  enviando: boolean;
  /** O backend recusou porque a conta tem sessão ativa em outro dispositivo. */
  sessaoAtivaEmOutroLugar: boolean;
  onSubmit: (senha: string, opcoes?: { encerrarOutraSessao?: boolean }) => void;
  onOutraConta: () => void;
}

/* ── Copy ────────────────────────────────────────────────────────────────────
 * Estado PADRÃO. A terceira linha não é conforto: é uma promessa que a arquitetura
 * cumpre literalmente — a fila reexecuta as requests que falharam e a sessão é
 * restaurada sem reload, então a tela e o trabalho em andamento continuam de pé.
 * O e2e do tripé (request original completa + empresa preservada + sem reload) é a
 * prova dela. Se um dia essa promessa deixar de ser verdade, esta linha vira mentira
 * e o teste que a sustenta é o que vai avisar. */
const TITULO_PADRAO = 'Continue de onde parou';
const CORPO_PADRAO = [
  'Seu acesso foi interrompido por segurança.',
  'Digite sua senha para continuar.',
  'Nada do que você estava fazendo foi perdido.',
];

/* Estado de ACESSO EXCLUSIVO. Copy reusada do `errorPermission` da tela de login
 * (Login/index.js:67-71) — já é texto aprovado pelo produto para exatamente este
 * evento; escrever outro criaria duas vozes para a mesma coisa. */
const TITULO_SESSAO_ATIVA = 'Acesso exclusivo por login';
const CORPO_SESSAO_ATIVA = ['Se outro dispositivo estiver conectado, ele será desconectado.'];

export const ReauthModal: React.FC<ReauthModalProps> = ({
  nomeExibicao,
  login,
  tentativasRestantes,
  erro,
  enviando,
  sessaoAtivaEmOutroLugar,
  onSubmit,
  onOutraConta,
}) => {
  const [senha, setSenha] = useState('');
  const campoRef = useRef<HTMLInputElement>(null);

  /* Foco no campo de senha ao abrir. Programático, e não `autoFocus`: a regra
     `jsx-a11y/no-autofocus` existe porque autofocus arrasta o leitor de tela para o meio
     de uma página que o usuário ainda está entendendo. Aqui o contexto é o oposto — a
     modal é bloqueante e tem um campo só, então levar o foco a ele é o comportamento
     correto. Fazer via ref entrega isso sem desligar a regra para o repositório. */
  useEffect(() => {
    campoRef.current?.focus();
  }, []);

  const handleSubmit = (evento: React.FormEvent) => {
    evento.preventDefault();
    if (enviando || !senha) return;
    // A MESMA senha é reenviada; o que muda é a autorização para derrubar a outra
    // sessão. O usuário não digita de novo — ele já digitou certo.
    onSubmit(senha, { encerrarOutraSessao: sessaoAtivaEmOutroLugar });
  };

  const titulo = sessaoAtivaEmOutroLugar ? TITULO_SESSAO_ATIVA : TITULO_PADRAO;
  const corpo = sessaoAtivaEmOutroLugar ? CORPO_SESSAO_ATIVA : CORPO_PADRAO;
  const rotuloDoBotao = sessaoAtivaEmOutroLugar ? 'Entrar aqui e encerrar a outra sessão' : 'Continuar';

  return (
    <Modal isOpen onClose={() => undefined} closeOnEsc={false} closeOnOverlayClick={false}>
      {({ isEntering, isExiting, contentRef }) => (
        <ModalBody
          ref={contentRef}
          data-entering={isEntering}
          data-exiting={isExiting}
          role="dialog"
          aria-modal="true"
          data-testid="reauth-modal"
        >
          <Header>
            <Title data-testid="reauth-title">{titulo}</Title>
          </Header>

          {/* `form` de verdade: é o que dá submit no Enter de graça, sem onKeyDown. */}
          <form onSubmit={handleSubmit}>
            <Content>
              <Message data-testid="reauth-message">
                {corpo.map((linha, indice) => (
                  <React.Fragment key={linha}>
                    {indice > 0 && <br />}
                    {linha}
                  </React.Fragment>
                ))}
              </Message>

              <Identificacao>
                <NomeUsuario data-testid="reauth-user">{nomeExibicao ?? login}</NomeUsuario>
                {nomeExibicao && login && nomeExibicao !== login && <LoginUsuario>{login}</LoginUsuario>}
              </Identificacao>

              <TS_FormGroup label="Senha">
                <StyledInput
                  type="password"
                  size="large"
                  width="100%"
                  ref={campoRef}
                  autoComplete="current-password"
                  data-testid="reauth-password"
                  value={senha}
                  disabled={enviando}
                  error={Boolean(erro)}
                  onChange={(evento: React.ChangeEvent<HTMLInputElement>) => setSenha(evento.target.value)}
                />
              </TS_FormGroup>

              {erro && (
                <ErroInline role="alert" data-testid="reauth-error">
                  {erro}
                </ErroInline>
              )}

              {/* Contador IMPLÍCITO (D4): não se diz "2 de 3" o tempo todo — só se avisa
                  quando resta uma, que é quando a informação muda o que o usuário faz
                  (parar e lembrar a senha, em vez de chutar de novo). */}
              {tentativasRestantes === 1 && (
                <AvisoUltimaTentativa data-testid="reauth-last-attempt">
                  Última tentativa. Na próxima falha você precisará entrar novamente.
                </AvisoUltimaTentativa>
              )}
            </Content>

            <Footer>
              <LinkOutraConta
                type="button"
                onClick={onOutraConta}
                disabled={enviando}
                data-testid="reauth-other-account"
              >
                Entrar com outra conta
              </LinkOutraConta>
              <Button type="submit" theme="primary" disabled={enviando || !senha} data-testid="reauth-submit">
                {enviando ? 'Entrando…' : rotuloDoBotao}
              </Button>
            </Footer>
          </form>
        </ModalBody>
      )}
    </Modal>
  );
};

export default ReauthModal;
